/**
 * Product card image carousel (collection grid)
 * ------------------------------------------------
 * Lets a shopper cycle through a product's images from the collection
 * grid without leaving the page: click arrows + a hover-revealed progress
 * bar on desktop, swipe + an always-visible progress bar on touch.
 *
 * Markup contract (see snippets/product-card-carousel.liquid, rendered by
 * snippets/product-grid-item.liquid):
 *   <div class="grid-product__carousel" data-product-carousel data-active-index="0">
 *     <div class="grid-product__carousel-track" data-carousel-track>
 *       <div class="grid-product__carousel-slide" data-active>...</div>
 *       <div class="grid-product__carousel-slide">...</div>
 *     </div>
 *     <button data-carousel-prev disabled>...</button>
 *     <button data-carousel-next>...</button>
 *     <div data-carousel-progress>
 *       <span class="grid-product__carousel-progress-segment is-active"></span>
 *       <span class="grid-product__carousel-progress-segment"></span>
 *     </div>
 *   </div>
 *
 * No dependencies, no per-card setup cost: everything is delegated off a
 * handful of document-level listeners so it works identically for cards
 * rendered on initial page load and cards injected later (filters,
 * infinite scroll, quick shop re-renders, etc).
 */
(function () {
  'use strict';

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function activate(carousel, index) {
    var track = carousel.querySelector('[data-carousel-track]');
    var slides = carousel.querySelectorAll('.grid-product__carousel-slide');
    if (!track || !slides.length) return;

    var max = slides.length - 1;
    if (index < 0) index = 0;
    if (index > max) index = max;

    track.style.transform = 'translateX(-' + index * 100 + '%)';
    carousel.setAttribute('data-active-index', String(index));

    for (var i = 0; i < slides.length; i++) {
      if (i === index) {
        slides[i].setAttribute('data-active', '');
      } else {
        slides[i].removeAttribute('data-active');
      }
    }

    // Progress bar fills cumulatively from the left through the current
    // slide (segments 0..index), not just the single active one, so it
    // reads as an actual progress indicator -- matching
    // https://toteme.com/en-us/collections/outerwear -- rather than a
    // position marker.
    var segments = carousel.querySelectorAll('.grid-product__carousel-progress-segment');
    for (var s = 0; s < segments.length; s++) {
      segments[s].classList.toggle('is-active', s <= index);
    }

    var prevBtn = carousel.querySelector('[data-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-carousel-next]');
    if (prevBtn) prevBtn.disabled = index === 0;
    if (nextBtn) nextBtn.disabled = index === max;
  }

  // --- Click arrows (desktop) -------------------------------------------

  document.addEventListener('click', function (event) {
    var btn = closest(event.target, '[data-carousel-prev], [data-carousel-next]');
    if (!btn) return;

    var carousel = closest(btn, '[data-product-carousel]');
    if (!carousel) return;

    // Stop this from bubbling into the card's own link.
    event.preventDefault();
    event.stopPropagation();

    var current = parseInt(carousel.getAttribute('data-active-index') || '0', 10);
    var next = btn.hasAttribute('data-carousel-next') ? current + 1 : current - 1;
    activate(carousel, next);
  });

  // Reset a card back to its first slide once the pointer leaves, so the
  // next hover always starts from the top again. Only meaningful for
  // hover-capable pointers -- `mouseleave` doesn't bubble, so this has to
  // listen on the capture phase.
  var hoverCapable = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (hoverCapable) {
    document.addEventListener(
      'mouseleave',
      function (event) {
        var wrapper = closest(event.target, '.grid__item-image-wrapper');
        if (!wrapper) return;
        var carousel = wrapper.querySelector('[data-product-carousel]');
        if (carousel && carousel.getAttribute('data-active-index') !== '0') {
          activate(carousel, 0);
        }
      },
      true
    );
  }

  // --- Swipe (touch) ------------------------------------------------------
  //
  // The theme's stretched product-card link overlay (.grid-product__link,
  // see product-grid-item.liquid) is disabled over the carousel on touch
  // (see same-collection-grid.liquid), specifically so touches here reach
  // this handling instead of being swallowed by that overlay. That means a
  // plain tap -- which the disabled overlay would otherwise have turned
  // into a normal link navigation -- has to be sent to the product page by
  // hand here (data-product-url, rendered by product-card-carousel.liquid).

  var SWIPE_THRESHOLD = 30; // px: a deliberate horizontal swipe
  var TAP_TOLERANCE = 10; // px: barely-moved touches still count as a tap

  var touch = null;

  document.addEventListener(
    'touchstart',
    function (event) {
      var carousel = closest(event.target, '[data-product-carousel]');
      if (!carousel || !event.touches.length) {
        touch = null;
        return;
      }
      touch = {
        carousel: carousel,
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        deltaX: 0,
        deltaY: 0,
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    function (event) {
      if (!touch || !event.touches.length) return;
      touch.deltaX = event.touches[0].clientX - touch.startX;
      touch.deltaY = event.touches[0].clientY - touch.startY;
    },
    { passive: true }
  );

  document.addEventListener('touchend', function () {
    if (!touch) return;

    var absX = Math.abs(touch.deltaX);
    var absY = Math.abs(touch.deltaY);
    var horizontal = absX > absY;

    if (horizontal && absX > SWIPE_THRESHOLD) {
      // Deliberate horizontal swipe: advance the carousel, don't navigate.
      var current = parseInt(touch.carousel.getAttribute('data-active-index') || '0', 10);
      var next = touch.deltaX < 0 ? current + 1 : current - 1;
      activate(touch.carousel, next);
    } else if (absX <= TAP_TOLERANCE && absY <= TAP_TOLERANCE) {
      // Barely moved: this is a tap, not a swipe or a page scroll. Send it
      // to the product page ourselves (see comment above).
      var url = touch.carousel.getAttribute('data-product-url');
      if (url) window.location.href = url;
    }
    // Otherwise this was a vertical drag that didn't clear either
    // threshold -- most likely the start of a page scroll. Leave it alone;
    // touch-action: pan-y on the track already lets the browser handle it.

    touch = null;
  });

  document.addEventListener('touchcancel', function () {
    touch = null;
  });
})();
